import { faker } from "@faker-js/faker";
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { enqueueSnackbar, useSnackbar } from "notistack";
import { z } from "zod";

import { withJWTClient } from "../connectClient";
import { gatewayUrl, withUpdateMask } from "../gateway";
import {
  fromGatewayFrame,
  fromGatewayFrameListing,
  fromGatewayFramePreviewListing,
  fromGatewayFrameVariantVersions,
  toGatewayFrameCreate,
  toGatewayFrameUpdate,
  type GatewayDuplicateFrameRequest,
  type GatewayFrame,
  type GatewayFrameVariantVersionsResponse,
  type GatewayRetrieveAllFramePreviewsResponse,
  type GatewayRetrieveAllFramesResponse,
} from "./gatewayFrames";
import {
  composeDragonTiles,
  fromGatewayInnerTile,
  type GatewayDragonTile,
  type GatewayDragonTilesResponse,
  type GatewayInnerTile,
  type GatewayInnerTilesResponse,
} from "./gatewayTiles";
import mockFrame1Html from "./mocks/mockFrame1";
import mockFrame2Html from "./mocks/mockFrame2";
import mockFrame3Mjml from "./mocks/mockMJMLJFrame";
import { filterExcludedFrames, patchFramesInFramesQueries } from "./utils";
import type { TextComposerFrameVariantContentJson } from "@/features/TextComposerV2/frame/frameOutput";
import type { TextComposerPreviewContent } from "@/features/TextComposerV2/preview/useMessagePreview";
import type {
  CreateFramePayloadParsed,
  UpdateFramePayloadParsed,
} from "@/frames/schemas";
import type {
  ChannelMedium,
  DragonTile,
  InnerTile,
  Frame,
  FrameVariant,
  SiteId,
  DragonTileId,
  InnerTileId,
  GetDragonTilesResponse,
  GetDragonTileResponse,
  UpdateDragonTilePayload,
  UpdateDragonTileResponse,
  CreateInnerTilePayload,
  CreateInnerTileResponse,
  DeleteInnerTileParams,
  FramePreviewManifest,
  GetFramePreviewsResponse,
  GetFramesResponse,
  InnerTileFrameRef,
  GetFrameVariantVersionsResponse,
  FrameVariantId,
} from "@/types/dragon-tiles";
import type { ProjectData } from "@grapesjs/studio-sdk";

export type {
  CreateFramePayloadParsed,
  CreateFrameVariantPayloadParsed,
  UpdateFramePayloadParsed,
} from "@/frames/schemas";

import { MOCK_PARENT_TILES } from "@/api/dragon-tiles/mocks/mockParentTiles";
import {
  previewUserContextKeyParts,
  type PreviewFallbackReason,
  type PreviewFillParams,
} from "@/api/dragon-tiles/previewUserContext";
import { FRAMES_PAGE_SIZE } from "@/components/TilesPagination/TilesPagination";
import { createFramePayloadSchema } from "@/frames/schemas";
import dayjs from "@/utils/configuredDayjs";
import { DemoModeBlockedError } from "@/utils/demoMode";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

// Add this near the top of the file, after imports
const USE_MOCKS = false; // Set to false to use real API

const CONNECT_API_V1_URL: string =
  import.meta.env?.VITE_CONNECT_API_V1_URL || "";

// --- Mock Data Stores ---
const mockFramesById: Record<string, Frame> = {};

// --- Mock Data Generation ---
const createMockFrameVariant = (frameId: InnerTileId): FrameVariant => {
  const htmlMockOptions = [mockFrame1Html, mockFrame2Html];
  const mjmlMockOptions: ProjectData[] = [mockFrame3Mjml];
  const isMJMLMock = faker.datatype.boolean();
  return {
    id: faker.string.uuid(),
    frameId,
    email: "",
    medium: faker.helpers.arrayElement(["email", "rcs", "sms", "mms"]),
    editor_type: faker.helpers.arrayElement([
      "grapesjs_email",
      "grapesjs_html",
      "html",
    ]),
    content_text: !isMJMLMock
      ? faker.helpers.arrayElement(htmlMockOptions)
      : "<h1>Empty</h1>",
    content_url: "",
    content_json: isMJMLMock ? faker.helpers.arrayElement(mjmlMockOptions) : {},
    create_date: faker.date.past().toISOString(),
    modify_date: faker.date.recent().toISOString(),
    deleted_date: null,
  };
};

const createMockFrame = (innerTileId: InnerTileId): Frame => {
  const variantCount = faker.number.int({ min: 1, max: 3 });
  const frame_variants: FrameVariant[] = [];
  for (let i = 0; i < variantCount; i++) {
    frame_variants.push(createMockFrameVariant(innerTileId));
  }
  const name = faker.company.catchPhrase();
  const frame: Frame = {
    id: faker.string.uuid(),
    inner_tile: innerTileId,
    name,
    is_active: faker.datatype.boolean(),
    status: faker.helpers.arrayElement(["draft", "published"]),
    approval_status: faker.helpers.arrayElement(["pending", "approved"]),
    slug: faker.helpers.slugify(name),
    frame_variants,
    create_date: faker.date.past().toISOString(),
    modify_date: faker.date.recent().toISOString(),
    deleted_date: null,
  };
  mockFramesById[frame.id] = frame;
  return frame;
};

const createMockInnerTile = (dragonTileId: DragonTileId): InnerTile => {
  const frameCount = faker.number.int({ min: 1, max: 2 });
  const frameRefs: InnerTileFrameRef[] = [];
  for (let i = 0; i < frameCount; i++) {
    const frame = createMockFrame(faker.string.uuid());
    frameRefs.push({
      id: frame.id,
      name: frame.name,
      is_active: frame.is_active,
      events: frame.metadata?.events ?? [],
    });
  }
  const innerTile: InnerTile = {
    id: faker.string.uuid(),
    dragonTileId,
    name: faker.commerce.department(),
    description: faker.lorem.sentence(),
    is_active: faker.datatype.boolean(),
    order: faker.number.int({ min: 0, max: 10 }),
    messages: [faker.lorem.sentence()],
    config: {
      cadenceInterval: faker.helpers.arrayElement(["day", "week", "month"]),
      cadenceStartAt: faker.number.int({ min: 0, max: 28 }),
    },
    frames: frameRefs,
    createdAt: faker.date.past().toISOString(),
    updatedAt: faker.date.recent().toISOString(),
    deletedAt: null,
  };
  return innerTile;
};

const createMockDragonTiles = (): DragonTile[] => {
  return MOCK_PARENT_TILES.map((mockTileInfo) => {
    return {
      id: mockTileInfo.id,
      name: mockTileInfo.name,
      slug: mockTileInfo.slug,
      site_id: mockTileInfo.site_id,
      category: mockTileInfo.category,
      is_active: mockTileInfo.is_active,
      config: mockTileInfo.config,
      order: mockTileInfo.order,
      is_activatable: faker.datatype.boolean(),
      is_clickable: faker.datatype.boolean(),
      analog_sequence: faker.datatype.boolean(),
      create_date: mockTileInfo.create_date,
      modify_date: mockTileInfo.modify_date,
      deleted_date: mockTileInfo.deleted_date,
      inner_tiles: Array.from(
        { length: faker.number.int({ min: 2, max: 5 }) },
        () => createMockInnerTile(faker.string.uuid()),
      ),
      needs_attention: [],
    };
  });
};

// In-memory store for mock data
const mockDragonTilesBySite: Record<SiteId, DragonTile[]> = {};
function getOrCreateMockDragonTiles(siteId: SiteId): DragonTile[] {
  if (!mockDragonTilesBySite[siteId]) {
    mockDragonTilesBySite[siteId] = createMockDragonTiles();
  }
  return mockDragonTilesBySite[siteId];
}

// --- Mock implementation ---
async function updateDragonTileMock(
  siteId: SiteId,
  dragonTileId: DragonTileId,
  payload: UpdateDragonTilePayload,
): Promise<UpdateDragonTileResponse> {
  const tiles = getOrCreateMockDragonTiles(siteId);
  const tileIndex = tiles.findIndex((tile) => tile.id === dragonTileId);
  if (tileIndex === -1) {
    return Promise.reject(new Error("DragonTile not found"));
  }

  const originalTile = tiles[tileIndex];
  const updateData = payload;

  const updatedTile: DragonTile = {
    ...originalTile,
    name: updateData.name ?? originalTile.name,
    is_active: updateData.is_active ?? originalTile.is_active,
    analog_sequence: updateData.analog_sequence ?? originalTile.analog_sequence,
    config: updateData.config ?? originalTile.config,
    inner_tiles: updateData.inner_tiles ?? originalTile.inner_tiles,
    modify_date: new Date().toISOString(),
  };

  tiles[tileIndex] = updatedTile;

  // Return a deep clone to ensure React Query detects changes
  return Promise.resolve(JSON.parse(JSON.stringify(updatedTile)) as DragonTile);
}

// --- Real implementation ---
async function updateDragonTileReal(
  siteId: SiteId,
  dragonTileId: DragonTileId,
  payload: UpdateDragonTilePayload,
): Promise<UpdateDragonTileResponse> {
  const parentTileFields = {
    name: payload.name,
    order: payload.order,
    category: payload.category,
    is_active: payload.is_active,
    analog_sequence: payload.analog_sequence,
    config: payload.config,
  };
  const hasParentTileFieldUpdates = Object.values(parentTileFields).some(
    (value) => value !== undefined,
  );

  const tasks: { label: string; request: Promise<unknown> }[] = [];
  if (hasParentTileFieldUpdates) {
    tasks.push({
      label: "tile settings",
      request: withJWTClient((client) =>
        client.patch(
          withUpdateMask(
            gatewayUrl(siteId, "dragon-tiles", dragonTileId),
            parentTileFields,
          ),
          parentTileFields,
        ),
      ),
    });
  }
  if (payload.inner_tiles) {
    // rj2's nested-PATCH reorder assigns order from array position and
    // partially updates each entry; re-expressed as one PATCH per inner tile.
    for (const [index, innerTile] of payload.inner_tiles.entries()) {
      const innerTileUpdate = {
        order: index,
        is_active: innerTile.is_active,
      };
      tasks.push({
        label: `inner tile ${innerTile.id}`,
        request: withJWTClient((client) =>
          client.patch(
            withUpdateMask(
              gatewayUrl(siteId, "inner-tiles", innerTile.id),
              innerTileUpdate,
            ),
            innerTileUpdate,
          ),
        ),
      });
    }
  }

  // allSettled, not all: these PATCHes have no shared transaction, so on a
  // partial failure we report exactly which tile(s) failed rather than surface
  // one opaque rejection while the other writes have already landed. The caller
  // invalidates the dragon-tile query on settle, so the UI re-syncs to the
  // persisted order regardless.
  const results = await Promise.allSettled(tasks.map((t) => t.request));
  // Demo-mode blocks rethrow as-is (typed), so no scary partial-failure toast.
  const demoBlocked = results.find(
    (r) => r.status === "rejected" && r.reason instanceof DemoModeBlockedError,
  );
  if (demoBlocked?.status === "rejected") throw demoBlocked.reason as Error;
  const failed = tasks.filter((_, i) => results[i].status === "rejected");
  if (failed.length > 0) {
    throw new Error(
      `Failed to update ${failed.map((t) => t.label).join(", ")}. Some changes may not have been saved; the list has been refreshed to show the saved order.`,
    );
  }
  return getGatewayDragonTileComposed(siteId, dragonTileId);
}

// --- Main export ---
export const updateDragonTile = (
  siteId: SiteId,
  dragonTileId: DragonTileId,
  payload: UpdateDragonTilePayload,
): Promise<UpdateDragonTileResponse> => {
  return USE_MOCKS
    ? updateDragonTileMock(siteId, dragonTileId, payload)
    : updateDragonTileReal(siteId, dragonTileId, payload);
};

// Query keys
export const dragonTilesKeys = {
  all: (siteId: SiteId) => ["dragonTiles", siteId] as const,
  byType: (siteId: SiteId, type: DragonTileTypeFilter) =>
    [...dragonTilesKeys.all(siteId), "type", type] as const,
  liteByType: (siteId: SiteId, type: DragonTileTypeFilter) =>
    [...dragonTilesKeys.all(siteId), "lite", type] as const,
  detail: (siteId: SiteId, dragonTileId: DragonTileId) =>
    [...dragonTilesKeys.all(siteId), "detail", dragonTileId] as const,
};

const EMAIL_PREVIEW_FALLBACK_ID = "unknown";

const getEmailPreviewKeyId = (
  value: unknown,
  fallback = EMAIL_PREVIEW_FALLBACK_ID,
) => {
  if (typeof value === "string" && value.length > 0) return value;
  if (typeof value === "number") return String(value);
  return fallback;
};

export const emailPreviewKeys = {
  all: (siteId: SiteId) => ["emailPreview", siteId] as const,
  frame: (siteId: SiteId, frameId: string) =>
    [...emailPreviewKeys.all(siteId), frameId] as const,
  detail: (
    siteId: SiteId,
    _contentText: string,
    config: Record<string, unknown>,
    previewFillParams?: PreviewFillParams,
  ) => {
    const frameId = getEmailPreviewKeyId(config.frame_id);
    const frameVariantId = getEmailPreviewKeyId(config.frame_variant_id);
    const revisionId = getEmailPreviewKeyId(config.revision_id);
    const ctxParts = previewUserContextKeyParts(previewFillParams);

    return [
      ...emailPreviewKeys.all(siteId),
      frameId,
      "email",
      frameVariantId,
      revisionId,
      ...ctxParts,
    ] as const;
  },
  live: (
    siteId: SiteId,
    contentText: string,
    config: Record<string, unknown>,
    previewFillParams?: PreviewFillParams,
    contentPlaintext?: string,
  ) =>
    [
      ...emailPreviewKeys.detail(
        siteId,
        contentText,
        config,
        previewFillParams,
      ),
      contentText,
      contentPlaintext ?? "",
      JSON.stringify(config),
    ] as const,
};

type TextPreviewMedium = Extract<ChannelMedium, "sms" | "mms" | "rcs">;

export type GetRendererFrameVariantTextPreviewRequest = {
  medium: TextPreviewMedium;
  content_json: TextComposerFrameVariantContentJson;
  config?: Record<string, unknown>;
  preview_fill_params?: PreviewFillParams;
};

export type RendererFrameVariantTextBody = {
  type?: "TEXT" | "IMAGE" | "DOCUMENT";
  text?: string;
  url?: string;
};

export type RendererFrameVariantTextButton = {
  type?: "OPEN_URL";
  text?: string;
  url?: string;
};

export type RendererFrameVariantTextSuggestion = {
  type?: string;
  text?: string;
  url?: string;
};

export type RendererFrameVariantTextTransformedContent = {
  body: RendererFrameVariantTextBody;
  buttons?: RendererFrameVariantTextButton[];
  suggestions?: RendererFrameVariantTextSuggestion[];
};

export type GetRendererFrameVariantTextPreviewResponse = {
  medium: TextPreviewMedium;
  transformedContent: RendererFrameVariantTextTransformedContent;
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const validationErrorBodySchema = z
  .object({
    non_field_errors: z.array(z.string()).optional(),
    name: z.array(z.string()).optional(),
  })
  .passthrough();
type ValidationErrorBody = z.infer<typeof validationErrorBodySchema>;

const parseValidationErrorBody = (
  data: unknown,
): ValidationErrorBody | null => {
  const result = validationErrorBodySchema.safeParse(data);
  return result.success ? result.data : null;
};

const textPreviewMediumSchema = z.enum(["sms", "mms", "rcs"], {
  errorMap: () => ({
    message: "Invalid text preview response: invalid medium",
  }),
});

const rendererFrameVariantTextBodySchema: z.ZodType<RendererFrameVariantTextBody> =
  z.custom<Record<string, unknown>>(isPlainRecord).transform((bodyRecord) => ({
    ...(bodyRecord.type === "TEXT" ||
    bodyRecord.type === "IMAGE" ||
    bodyRecord.type === "DOCUMENT"
      ? { type: bodyRecord.type }
      : {}),
    ...(typeof bodyRecord.text === "string" ? { text: bodyRecord.text } : {}),
    ...(typeof bodyRecord.url === "string" ? { url: bodyRecord.url } : {}),
  }));

const rendererFrameVariantTextButtonSchema: z.ZodType<RendererFrameVariantTextButton> =
  z.custom<Record<string, unknown>>(isPlainRecord).transform((button) => ({
    ...(button.type === "OPEN_URL" ? { type: "OPEN_URL" as const } : {}),
    ...(typeof button.text === "string" ? { text: button.text } : {}),
    ...(typeof button.url === "string" ? { url: button.url } : {}),
  }));

const rendererFrameVariantTextButtonsSchema = z
  .array(z.unknown())
  .optional()
  .transform((buttonsSource): RendererFrameVariantTextButton[] | undefined => {
    if (!Array.isArray(buttonsSource) || buttonsSource.length === 0) {
      return undefined;
    }
    const parsed = buttonsSource
      .filter(isPlainRecord)
      .map((row) => rendererFrameVariantTextButtonSchema.parse(row));
    return parsed.length > 0 ? parsed : undefined;
  });

const rendererFrameVariantTextSuggestionsSchema = z
  .array(z.unknown())
  .optional()
  .transform((source): RendererFrameVariantTextSuggestion[] | undefined => {
    if (!Array.isArray(source) || source.length === 0) return undefined;
    const parsed = source.filter(isPlainRecord).map((row) => ({
      ...(typeof row.type === "string" ? { type: row.type } : {}),
      ...(typeof row.text === "string" ? { text: row.text } : {}),
      ...(typeof row.url === "string" ? { url: row.url } : {}),
    }));
    return parsed.length > 0 ? parsed : undefined;
  });

/** Zod schema for `POST .../preview/frame-variant` text (sms/mms/rcs) responses. */
export const getRendererFrameVariantTextPreviewResponseSchema = z
  .custom<Record<string, unknown>>(isPlainRecord, {
    message: "Invalid text preview response: expected object",
  })
  .transform((root) => {
    const source = isPlainRecord(root.text_preview) ? root.text_preview : root;
    const transformedContentSource =
      source.transformed_content ??
      (isPlainRecord(source.transformed)
        ? source.transformed.content
        : undefined) ??
      (isPlainRecord(source.content_json) &&
      isPlainRecord(source.content_json.transformed)
        ? source.content_json.transformed.content
        : undefined);

    if (!isPlainRecord(transformedContentSource)) {
      throw new Error(
        "Invalid text preview response: missing transformed content",
      );
    }

    return {
      medium: source.medium ?? root.medium,
      transformedContentSource,
    };
  })
  .pipe(
    z.object({
      medium: textPreviewMediumSchema,
      transformedContentSource: z.record(z.string(), z.unknown()),
    }),
  )
  .transform(
    ({
      medium,
      transformedContentSource,
    }): GetRendererFrameVariantTextPreviewResponse => {
      const body = transformedContentSource.body;
      if (!isPlainRecord(body)) {
        throw new Error("Invalid text preview response: missing body");
      }

      const parsedBody = rendererFrameVariantTextBodySchema.parse(body);
      const parsedButtons = rendererFrameVariantTextButtonsSchema.parse(
        transformedContentSource.buttons,
      );
      const parsedSuggestions = rendererFrameVariantTextSuggestionsSchema.parse(
        transformedContentSource.suggestions,
      );

      return {
        medium,
        transformedContent: {
          body: parsedBody,
          ...(parsedButtons ? { buttons: parsedButtons } : {}),
          ...(parsedSuggestions ? { suggestions: parsedSuggestions } : {}),
        },
      };
    },
  );

export async function getRenderedFrameVariantTextPreview(
  siteId: string,
  body: GetRendererFrameVariantTextPreviewRequest,
): Promise<GetRendererFrameVariantTextPreviewResponse> {
  const response = await withJWTClient((client) =>
    client.post(`${CONNECT_API_V1_URL}/sites/${siteId}/preview/frame-variant`, {
      medium: body.medium,
      content_json: body.content_json,
      ...(body.config !== undefined ? { config: body.config } : {}),
      ...(body.preview_fill_params
        ? { preview_fill_params: body.preview_fill_params }
        : {}),
    }),
  );

  return getRendererFrameVariantTextPreviewResponseSchema.parse(response.data);
}

export const textPreviewKeys = {
  all: (siteId: SiteId) => ["textPreview", siteId] as const,
  frame: (siteId: SiteId, frameId: string) =>
    [...textPreviewKeys.all(siteId), frameId] as const,
  detail: (
    siteId: SiteId,
    medium: TextPreviewMedium,
    contentSignature: string,
    config: Record<string, unknown>,
    previewFillParams?: PreviewFillParams,
  ) => {
    const frameId = getEmailPreviewKeyId(config.frame_id);
    const frameVariantId = getEmailPreviewKeyId(config.frame_variant_id);
    const revisionId = getEmailPreviewKeyId(config.revision_id);
    const ctxParts = previewUserContextKeyParts(previewFillParams);

    return [
      ...textPreviewKeys.frame(siteId, frameId),
      medium,
      frameVariantId,
      revisionId,
      contentSignature,
      ...ctxParts,
    ] as const;
  },
};

// React Query hooks
const sortDragonTilesByOrder = (data: GetDragonTilesResponse) =>
  data.sort((a, b) => a.order - b.order);

export function useDragonTiles(
  siteId: SiteId,
  options?: Omit<
    UseQueryOptions<GetDragonTilesResponse, Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: dragonTilesKeys.all(siteId),
    queryFn: () => getDragonTiles(siteId),
    enabled: !!siteId,
    select: sortDragonTilesByOrder,
    ...options,
  });
}

export function useDragonTilesByType(
  siteId: SiteId,
  type: DragonTileTypeFilter,
  options?: Omit<
    UseQueryOptions<GetDragonTilesResponse, Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: dragonTilesKeys.byType(siteId, type),
    queryFn: () => getDragonTilesByType(siteId, type),
    enabled: !!siteId,
    select: sortDragonTilesByOrder,
    ...options,
  });
}

export function useDragonTilesLite(
  siteId: SiteId,
  type: DragonTileTypeFilter,
  options?: Omit<
    UseQueryOptions<GetDragonTilesResponse, Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: dragonTilesKeys.liteByType(siteId, type),
    queryFn: () => getDragonTilesLite(siteId, type),
    enabled: !!siteId,
    select: sortDragonTilesByOrder,
    ...options,
  });
}

export function useDragonTile(
  siteId: SiteId,
  dragonTileId: DragonTileId,
  options?: Omit<
    UseQueryOptions<GetDragonTileResponse, Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: dragonTilesKeys.detail(siteId, dragonTileId),
    queryFn: () => getDragonTile(siteId, dragonTileId),
    enabled: !!siteId && !!dragonTileId,
    ...options,
  });
}

export function useUpdateDragonTile(options?: {
  successMessage?: string;
  suppressSuccessToast?: boolean;
  optimisticActivation?: boolean;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    UpdateDragonTileResponse,
    Error,
    {
      siteId: SiteId;
      dragonTileId: DragonTileId;
      payload: UpdateDragonTilePayload;
    },
    { previous?: DragonTile }
  >({
    mutationFn: ({ siteId, dragonTileId, payload }) =>
      updateDragonTile(siteId, dragonTileId, payload),
    onMutate: async ({ siteId, dragonTileId, payload }) => {
      const nextIsActive = payload.is_active;
      if (!options?.optimisticActivation || nextIsActive === undefined) {
        return {};
      }
      const queryKey = dragonTilesKeys.detail(siteId, dragonTileId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<DragonTile>(queryKey);
      if (previous) {
        queryClient.setQueryData<DragonTile>(queryKey, {
          ...previous,
          is_active: nextIsActive,
        });
      }
      return { previous };
    },
    onSuccess: () => {
      if (options?.suppressSuccessToast) return;
      enqueueSnackbar(
        options?.successMessage ?? "Dragon Tile updated successfully",
        {
          variant: "success",
        },
      );
    },
    onSettled: (_, __, { siteId, dragonTileId }) => {
      void queryClient.invalidateQueries({
        queryKey: dragonTilesKeys.all(siteId),
      });
      void queryClient.invalidateQueries({
        queryKey: dragonTilesKeys.detail(siteId, dragonTileId),
      });
    },
    onError: (error, { siteId, dragonTileId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          dragonTilesKeys.detail(siteId, dragonTileId),
          context.previous,
        );
      }
      enqueueErrorSnackbar(error, "Failed to update Dragon Tile");
    },
  });
}

export function useCreateInnerTile() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    CreateInnerTileResponse,
    Error,
    {
      siteId: SiteId;
      dragonTileId: DragonTileId;
      payload: CreateInnerTilePayload;
    }
  >({
    mutationFn: ({ siteId, dragonTileId, payload }) =>
      createInnerTile(siteId, dragonTileId, payload),
    onSuccess: () => {
      enqueueSnackbar("Audience added successfully", {
        variant: "success",
      });
    },
    onSettled: (_, __, { siteId, dragonTileId }) => {
      void queryClient.invalidateQueries({
        queryKey: dragonTilesKeys.detail(siteId, dragonTileId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to add Audience");
    },
  });
}

export function useDeleteInnerTile() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<void, Error, DeleteInnerTileParams>({
    mutationFn: ({ siteId, dragonTileId, innerTileId }) =>
      deleteInnerTile(siteId, dragonTileId, innerTileId),
    onSuccess: () => {
      enqueueSnackbar("Audience removed successfully", {
        variant: "success",
      });
    },
    onSettled: (_, __, { siteId, dragonTileId }) => {
      void queryClient.invalidateQueries({
        queryKey: dragonTilesKeys.detail(siteId, dragonTileId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to remove Audience");
    },
  });
}

function invalidateAfterFrameWrite(
  queryClient: QueryClient,
  siteId: SiteId,
  createdFrame: Frame | undefined,
) {
  // Invalidate tile-related queries
  void queryClient.invalidateQueries({
    queryKey: ["frame", siteId],
  });
  // Also invalidate dragon tile queries since tiles are part of inner tiles
  void queryClient.invalidateQueries({
    queryKey: dragonTilesKeys.all(siteId),
  });
  // Invalidate all tiles query since a new tile was created
  void queryClient.invalidateQueries({
    queryKey: ["allFrames", siteId],
  });

  // Invalidate only the specific frame's variant previews instead of all bulk previews
  if (createdFrame) {
    invalidateFrameVariantPreviews(queryClient, siteId, createdFrame);
  } else {
    // Fallback: if frame not available, invalidate all bulk previews
    void queryClient.invalidateQueries({
      queryKey: ["bulkFrameVariantPreviews", siteId],
    });
  }

  void queryClient.invalidateQueries({
    queryKey: ["frames", siteId],
  });

  invalidateFramePreviews(queryClient, siteId);

  // Invalidate framesWithPreviews query used in creative sphere
  void queryClient.invalidateQueries({
    queryKey: ["framesWithPreviews", siteId],
  });
}

function handleFrameUniqueNameError(
  error: Error,
  enqueueSnackbar: ReturnType<typeof useSnackbar>["enqueueSnackbar"],
): boolean {
  if (isAxiosError(error)) {
    const responseData = parseValidationErrorBody(error.response?.data);
    if (responseData?.non_field_errors) {
      const hasUniqueError = responseData.non_field_errors.some((message) =>
        message.includes("The fields inner_tile, name must make a unique set"),
      );
      if (hasUniqueError) {
        enqueueSnackbar("A tile with this name already exists.", {
          variant: "error",
        });
        return true;
      }
    }
  }
  return false;
}

export function useCreateFrame() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    Frame,
    Error,
    {
      siteId: SiteId;
      payload: CreateFramePayloadParsed;
    }
  >({
    mutationFn: ({ siteId, payload }) => createFrame(siteId, payload),
    onError: (error) => {
      if (handleFrameUniqueNameError(error, enqueueSnackbar)) return;
      enqueueErrorSnackbar(error, "Failed to create Tile");
    },
    onSettled: (createdFrame, __, { siteId }) => {
      invalidateAfterFrameWrite(queryClient, siteId, createdFrame);
    },
  });
}

export function useDuplicateFrame() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    Frame,
    Error,
    {
      siteId: SiteId;
      frameId: string;
      payload: DuplicateFramePayload;
    }
  >({
    mutationFn: ({ siteId, frameId, payload }) =>
      duplicateFrame(siteId, frameId, payload),
    onError: (error) => {
      if (handleFrameUniqueNameError(error, enqueueSnackbar)) return;
      enqueueErrorSnackbar(error, "Failed to duplicate Tile");
    },
    onSettled: (createdFrame, __, { siteId }) => {
      invalidateAfterFrameWrite(queryClient, siteId, createdFrame);
    },
  });
}

export const dragonTilesQueryOptions = (siteId: string) => {
  return {
    queryKey: dragonTilesKeys.all(siteId),
    queryFn: () => getDragonTiles(siteId),
    staleTime: 30_000, // 30 seconds - prevents refetch right after loader prefetch
  };
};

export const dragonTilesByTypeQueryOptions = (
  siteId: string,
  type: DragonTileTypeFilter,
) => {
  return {
    queryKey: dragonTilesKeys.byType(siteId, type),
    queryFn: () => getDragonTilesByType(siteId, type),
    staleTime: 30_000,
  };
};

/**
 * Connector/name-only consumers: skips the site-wide /frames fetch the composed
 * query needs to attach frame refs to inner tiles.
 */
export const dragonTilesLiteByTypeQueryOptions = (
  siteId: string,
  type: DragonTileTypeFilter,
) => {
  return {
    queryKey: dragonTilesKeys.liteByType(siteId, type),
    queryFn: () => getDragonTilesLite(siteId, type),
    staleTime: 30_000,
  };
};

export const dragonTileQueryOptions = (
  siteId: string,
  dragonTileId: string,
) => {
  return {
    queryKey: dragonTilesKeys.detail(siteId, dragonTileId),
    queryFn: () => getDragonTile(siteId, dragonTileId),
    staleTime: 30_000, // 30 seconds - prevents refetch right after loader prefetch
  };
};

// --- Mock implementation ---
async function getDragonTilesMock(
  siteId: SiteId,
): Promise<GetDragonTilesResponse> {
  // Return a deep clone to ensure React Query detects changes
  const tiles = getOrCreateMockDragonTiles(siteId);
  return Promise.resolve(JSON.parse(JSON.stringify(tiles)) as DragonTile[]);
}

export type DragonTileTypeFilter = "all" | "basic" | "connector" | "byoc";

async function getGatewayDragonTilesComposed(
  siteId: SiteId,
  type?: DragonTileTypeFilter,
): Promise<DragonTile[]> {
  const [tilesResponse, innerTilesResponse, framesResponse] = await Promise.all(
    [
      withJWTClient((client) =>
        client.get<GatewayDragonTilesResponse>(
          gatewayUrl(siteId, "dragon-tiles"),
          type ? { params: { type } } : undefined,
        ),
      ),
      withJWTClient((client) =>
        client.get<GatewayInnerTilesResponse>(
          gatewayUrl(siteId, "inner-tiles"),
        ),
      ),
      withJWTClient((client) =>
        client.get<GatewayRetrieveAllFramesResponse>(
          gatewayUrl(siteId, "frames"),
        ),
      ),
    ],
  );
  return composeDragonTiles(
    tilesResponse.data.dragon_tiles ?? [],
    innerTilesResponse.data.inner_tiles ?? [],
    framesResponse.data.frames ?? [],
  );
}

async function getGatewayDragonTileComposed(
  siteId: SiteId,
  dragonTileId: DragonTileId,
): Promise<DragonTile> {
  const [tileResponse, innerTilesResponse, framesResponse] = await Promise.all([
    withJWTClient((client) =>
      client.get<GatewayDragonTile>(
        gatewayUrl(siteId, "dragon-tiles", dragonTileId),
      ),
    ),
    withJWTClient((client) =>
      client.get<GatewayInnerTilesResponse>(gatewayUrl(siteId, "inner-tiles"), {
        params: { dragon_tile_id: dragonTileId },
      }),
    ),
    withJWTClient((client) =>
      client.get<GatewayRetrieveAllFramesResponse>(
        gatewayUrl(siteId, "frames"),
        { params: { dragon_tile: dragonTileId } },
      ),
    ),
  ]);
  const [composed] = composeDragonTiles(
    [tileResponse.data],
    innerTilesResponse.data.inner_tiles ?? [],
    framesResponse.data.frames ?? [],
  );
  return composed;
}

// --- Real implementation ---
async function getDragonTilesReal(
  siteId: SiteId,
): Promise<GetDragonTilesResponse> {
  return getGatewayDragonTilesComposed(siteId);
}

export async function getDragonTilesByType(
  siteId: SiteId,
  type: DragonTileTypeFilter,
): Promise<GetDragonTilesResponse> {
  return getGatewayDragonTilesComposed(siteId, type);
}

export async function getDragonTilesLite(
  siteId: SiteId,
  type: DragonTileTypeFilter,
): Promise<GetDragonTilesResponse> {
  const [tilesResponse, innerTilesResponse] = await Promise.all([
    withJWTClient((client) =>
      client.get<GatewayDragonTilesResponse>(
        gatewayUrl(siteId, "dragon-tiles"),
        { params: { type } },
      ),
    ),
    withJWTClient((client) =>
      client.get<GatewayInnerTilesResponse>(gatewayUrl(siteId, "inner-tiles")),
    ),
  ]);
  return composeDragonTiles(
    tilesResponse.data.dragon_tiles ?? [],
    innerTilesResponse.data.inner_tiles ?? [],
    [],
  );
}

// --- Main export ---
export const getDragonTiles = (
  siteId: SiteId,
): Promise<GetDragonTilesResponse> => {
  return USE_MOCKS ? getDragonTilesMock(siteId) : getDragonTilesReal(siteId);
};

// --- Mock implementation ---
async function getDragonTileMock(
  siteId: SiteId,
  dragonTileId: DragonTileId,
): Promise<GetDragonTileResponse> {
  const tiles = getOrCreateMockDragonTiles(siteId);
  const dragonTile = tiles.find((tile) => tile.id === dragonTileId);
  if (!dragonTile) {
    return Promise.reject(new Error("DragonTile not found"));
  }
  // Return a deep clone to ensure React Query detects changes
  return Promise.resolve(JSON.parse(JSON.stringify(dragonTile)) as DragonTile);
}

// --- Real implementation ---
async function getDragonTileReal(
  siteId: SiteId,
  dragonTileId: DragonTileId,
): Promise<GetDragonTileResponse> {
  return getGatewayDragonTileComposed(siteId, dragonTileId);
}

// --- Main export ---
export const getDragonTile = (
  siteId: SiteId,
  dragonTileId: DragonTileId,
): Promise<GetDragonTileResponse> => {
  return USE_MOCKS
    ? getDragonTileMock(siteId, dragonTileId)
    : getDragonTileReal(siteId, dragonTileId);
};

// --- Mock implementation ---
async function createInnerTileMock(
  siteId: SiteId,
  dragonTileId: DragonTileId,
  payload: CreateInnerTilePayload,
): Promise<CreateInnerTileResponse> {
  const tiles = getOrCreateMockDragonTiles(siteId);
  const tileIndex = tiles.findIndex((tile) => tile.id === dragonTileId);
  if (tileIndex === -1) {
    return Promise.reject(new Error("DragonTile not found"));
  }

  const { innerTile: createData } = payload;

  const newInnerTile: InnerTile = {
    id: faker.string.uuid(),
    dragonTileId,
    name: createData.name,
    is_active: createData.is_active,
    audience_id: createData.audience_id,
    config: createData.config,
    description: faker.lorem.sentence(),
    order: tiles[tileIndex].inner_tiles.length,
    messages: [],
    frames: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    deletedAt: null,
  };

  tiles[tileIndex].inner_tiles.push(newInnerTile);

  // Return a deep clone to ensure React Query detects changes
  return Promise.resolve({
    innerTile: JSON.parse(JSON.stringify(newInnerTile)) as InnerTile,
  });
}

// --- Real implementation ---
async function createInnerTileReal(
  siteId: SiteId,
  dragonTileId: DragonTileId,
  payload: CreateInnerTilePayload,
): Promise<CreateInnerTileResponse> {
  // rj2 assigns order server-side as the live-tile count; the flat gateway
  // create takes an explicit order, so compute the same value here. This
  // read-then-write can race two concurrent creates to the same order, but
  // connect-api stores the supplied order verbatim (it doesn't auto-assign),
  // so omitting it would collapse every tile to 0. Accepted: inner-tile
  // creation is a low-concurrency admin action; a transactional server-side
  // assignment is the proper fix if this ever becomes contended.
  const siblingsResponse = await withJWTClient((client) =>
    client.get<GatewayInnerTilesResponse>(gatewayUrl(siteId, "inner-tiles"), {
      params: { dragon_tile_id: dragonTileId },
    }),
  );
  const order = (siblingsResponse.data.inner_tiles ?? []).length;
  const response = await withJWTClient((client) =>
    client.post<GatewayInnerTile>(gatewayUrl(siteId, "inner-tiles"), {
      dragon_tile_id: dragonTileId,
      rj_dragon_tile_id: dragonTileId,
      name: payload.innerTile.name,
      is_active: payload.innerTile.is_active,
      audience_id: payload.innerTile.audience_id,
      config: payload.innerTile.config,
      order,
    }),
  );
  return { innerTile: fromGatewayInnerTile(response.data, []) };
}

// --- Main export ---
export const createInnerTile = (
  siteId: SiteId,
  dragonTileId: DragonTileId,
  payload: CreateInnerTilePayload,
): Promise<CreateInnerTileResponse> => {
  return USE_MOCKS
    ? createInnerTileMock(siteId, dragonTileId, payload)
    : createInnerTileReal(siteId, dragonTileId, payload);
};

// --- Mock implementation ---
async function deleteInnerTileMock(
  siteId: SiteId,
  dragonTileId: DragonTileId,
  innerTileId: InnerTileId,
): Promise<void> {
  const tiles = getOrCreateMockDragonTiles(siteId);
  const tile = tiles.find((t) => t.id === dragonTileId);
  if (!tile) return Promise.reject(new Error("DragonTile not found"));

  tile.inner_tiles = tile.inner_tiles.filter((it) => it.id !== innerTileId);
}

// --- Real implementation ---
async function deleteInnerTileReal(
  siteId: SiteId,
  innerTileId: InnerTileId,
): Promise<void> {
  await withJWTClient((client) =>
    client.delete(gatewayUrl(siteId, "inner-tiles", innerTileId)),
  );
}

// --- Main export ---
export const deleteInnerTile = (
  siteId: SiteId,
  dragonTileId: DragonTileId,
  innerTileId: InnerTileId,
): Promise<void> => {
  return USE_MOCKS
    ? deleteInnerTileMock(siteId, dragonTileId, innerTileId)
    : deleteInnerTileReal(siteId, innerTileId);
};

// --- Real implementation ---
async function createFrameReal(
  siteId: SiteId,
  payload: CreateFramePayloadParsed,
): Promise<Frame> {
  const response = await withJWTClient((client) =>
    client.post<GatewayFrame>(
      gatewayUrl(siteId, "frames"),
      toGatewayFrameCreate(payload),
    ),
  );
  return fromGatewayFrame(response.data);
}

// --- Main export ---
export const createFrame = (
  siteId: SiteId,
  payload: CreateFramePayloadParsed,
): Promise<Frame> => {
  return createFrameReal(siteId, payload);
};

export interface DuplicateFramePayload {
  name?: string;
  inner_tile_id?: string;
  mediums?: ChannelMedium[];
  exclude_metadata?: boolean;
}

async function duplicateFrameReal(
  siteId: SiteId,
  frameId: string,
  payload: DuplicateFramePayload,
): Promise<Frame> {
  const body: GatewayDuplicateFrameRequest = {
    name: payload.name,
    inner_tile_id: payload.inner_tile_id,
    mediums: payload.mediums,
    exclude_metadata: payload.exclude_metadata,
  };
  const response = await withJWTClient((client) =>
    client.post<GatewayFrame>(
      gatewayUrl(siteId, "frames", frameId, "duplicate"),
      body,
    ),
  );
  return fromGatewayFrame(response.data);
}

export const duplicateFrame = (
  siteId: SiteId,
  frameId: string,
  payload: DuplicateFramePayload,
): Promise<Frame> => {
  return duplicateFrameReal(siteId, frameId, payload);
};

// --- Mock implementation ---
async function getFrameMock(siteId: SiteId, frameId: string): Promise<Frame> {
  const frame = mockFramesById[frameId];
  if (!frame) return Promise.reject(new Error("Tile not found"));
  // Return a deep clone
  return Promise.resolve(JSON.parse(JSON.stringify(frame)) as Frame);
}

// --- Real implementation ---
async function getFrameReal(siteId: SiteId, frameId: string): Promise<Frame> {
  const response = await withJWTClient((client) =>
    client.get<GatewayFrame>(gatewayUrl(siteId, "frames", frameId)),
  );
  return fromGatewayFrame(response.data);
}

export const getFrame = (siteId: SiteId, frameId: string): Promise<Frame> => {
  return USE_MOCKS
    ? getFrameMock(siteId, frameId)
    : getFrameReal(siteId, frameId);
};

export const deleteFrame = async (
  siteId: SiteId,
  frameId: string,
): Promise<void> => {
  await withJWTClient((client) =>
    client.delete(gatewayUrl(siteId, "frames", frameId)),
  );
};

export const frameQueryOptions = (siteId: string, frameId: string) => ({
  queryKey: ["frame", siteId, frameId],
  queryFn: () => getFrame(siteId, frameId),
});

export const emailPreviewQueryOptions = (
  siteId: string,
  contentText: string,
  config: Record<string, unknown>,
  previewFillParams?: PreviewFillParams,
  contentPlaintext?: string,
): {
  queryKey: QueryKey;
  queryFn: () => Promise<GetRendererFrameVariantHTMLPreviewResponse>;
  retry: number;
  staleTime: number;
  gcTime: number;
  refetchOnMount: "always";
} => ({
  queryKey: emailPreviewKeys.detail(
    siteId,
    contentText,
    config,
    previewFillParams,
  ),
  queryFn: async () => {
    const body: GetRendererFrameVariantHTMLPreviewRequest = {
      medium: "email",
      content_text: contentText,
      ...(contentPlaintext !== undefined
        ? { content_plaintext: contentPlaintext }
        : {}),
      config,
    };
    if (previewFillParams) {
      body.preview_fill_params = {
        ...previewFillParams,
      };
    }
    return getRenderedFrameVariantHTMLPreview(siteId, body);
  },
  retry: 1,
  staleTime: 0,
  gcTime: 0,
  refetchOnMount: "always",
});

// Stays under `emailPreviewKeys.frame(...)` so frame-level invalidation still matches.
export const liveEmailPreviewQueryOptions = (
  siteId: string,
  contentText: string,
  config: Record<string, unknown>,
  previewFillParams?: PreviewFillParams,
  contentPlaintext?: string,
) => ({
  ...emailPreviewQueryOptions(
    siteId,
    contentText,
    config,
    previewFillParams,
    contentPlaintext,
  ),
  queryKey: emailPreviewKeys.live(
    siteId,
    contentText,
    config,
    previewFillParams,
    contentPlaintext,
  ),
});

export const textPreviewQueryOptions = (
  siteId: string,
  body: GetRendererFrameVariantTextPreviewRequest,
  config: Record<string, unknown> = {},
  previewFillParams?: PreviewFillParams,
): {
  queryKey: QueryKey;
  queryFn: () => Promise<GetRendererFrameVariantTextPreviewResponse>;
  retry: number;
  staleTime: number;
  gcTime: number;
  refetchOnMount: "always";
} => {
  const contentSignature = JSON.stringify(body.content_json);
  return {
    queryKey: textPreviewKeys.detail(
      siteId,
      body.medium,
      contentSignature,
      config,
      previewFillParams,
    ),
    queryFn: () =>
      getRenderedFrameVariantTextPreview(siteId, {
        ...body,
        config,
        ...(previewFillParams
          ? { preview_fill_params: previewFillParams }
          : {}),
      }),
    retry: 1,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  };
};

// --- Mock implementation ---
async function updateFrameMock(
  siteId: SiteId,
  frameId: string,
  payload: UpdateFramePayloadParsed,
): Promise<Frame> {
  const frame = mockFramesById[frameId];
  if (!frame) return Promise.reject(new Error("Tile not found"));
  const updatedFrame: Frame = {
    ...frame,
    ...(payload as Partial<Frame>),
    updatedAt: new Date().toISOString(),
  };
  mockFramesById[frameId] = updatedFrame;
  return Promise.resolve(JSON.parse(JSON.stringify(updatedFrame)) as Frame);
}

// --- Real implementation ---
async function updateFrameReal(
  siteId: SiteId,
  frameId: string,
  payload: UpdateFramePayloadParsed,
): Promise<Frame> {
  const { frame, updateMask } = toGatewayFrameUpdate(payload);
  const url = gatewayUrl(siteId, "frames", frameId);
  // Full-set variant payload → PUT (ReplaceFrame, no mask). Frame-only edits stay PATCH.
  // The nested frame_variants list is authoritative regardless of update_mask, so a partial
  // list would wipe data — routing full sets to PUT keeps PATCH free of variants (FOG-2267).
  const params = updateMask
    ? `?${new URLSearchParams({ update_mask: updateMask }).toString()}`
    : "";
  const response = await withJWTClient((client) =>
    payload.frame_variants !== undefined
      ? client.put<GatewayFrame>(url, frame)
      : client.patch<GatewayFrame>(`${url}${params}`, frame),
  );
  return fromGatewayFrame(response.data);
}

export const updateFrame = (
  siteId: SiteId,
  frameId: string,
  payload: UpdateFramePayloadParsed,
): Promise<Frame> => {
  return USE_MOCKS
    ? updateFrameMock(siteId, frameId, payload)
    : updateFrameReal(siteId, frameId, payload);
};

// --- React Query mutation hook ---
export function useUpdateFrame() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation<
    Frame,
    Error,
    {
      siteId: SiteId;
      frameId: string;
      payload: UpdateFramePayloadParsed;
      silent?: boolean;
    }
  >({
    mutationFn: ({ siteId, frameId, payload }): Promise<Frame> =>
      updateFrame(siteId, frameId, payload),
    onSuccess: (updatedFrame, variables) => {
      if (!variables.silent) {
        enqueueSnackbar("Tile updated!", {
          variant: "success",
        });
      }

      // Optimistically update the allFrames cache
      queryClient.setQueryData(
        ["allFrames", variables.siteId],
        (oldData: Frame[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((frame) =>
            frame.id === variables.frameId ? updatedFrame : frame,
          );
        },
      );

      patchFramesInFramesQueries(queryClient, variables.siteId, (frame) =>
        frame.id === variables.frameId ? updatedFrame : undefined,
      );

      // Update all framesWithPreviews queries for this site
      queryClient.setQueriesData<{ frame: Frame; renderedPreview: string }[]>(
        { queryKey: ["framesWithPreviews", variables.siteId] },
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((item) =>
            item.frame.id === variables.frameId
              ? { ...item, frame: updatedFrame }
              : item,
          );
        },
      );
    },
    onSettled: (updatedFrame, __, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["frame", variables.siteId, variables.frameId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["allFrames", variables.siteId],
      });
      // Invalidate only the specific frame's variant previews instead of all bulk previews
      if (updatedFrame) {
        invalidateFrameVariantPreviews(
          queryClient,
          variables.siteId,
          updatedFrame,
        );
      } else {
        // Fallback: if frame not available, invalidate all bulk previews
        void queryClient.invalidateQueries({
          queryKey: ["bulkFrameVariantPreviews", variables.siteId],
        });
      }
      void queryClient.invalidateQueries({
        queryKey: ["frames", variables.siteId],
      });
      invalidateFramePreviews(queryClient, variables.siteId);
      void queryClient.invalidateQueries({
        queryKey: ["framesWithPreviews", variables.siteId],
      });
      void queryClient.invalidateQueries({
        queryKey: emailPreviewKeys.frame(variables.siteId, variables.frameId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["bulkEmailBroadcastPreviews", variables.siteId],
      });
      void queryClient.invalidateQueries({
        queryKey: dragonTilesKeys.all(variables.siteId),
      });
    },
    onError: (error) => {
      // Handle specific API validation errors
      if (isAxiosError(error)) {
        const responseData = parseValidationErrorBody(error.response?.data);

        // Check for uniqueness error in non_field_errors
        if (responseData?.non_field_errors) {
          const hasUniqueError = responseData.non_field_errors.some((message) =>
            message.includes(
              "The fields inner_tile, name must make a unique set",
            ),
          );
          if (hasUniqueError) {
            enqueueSnackbar("A tile with this name already exists.", {
              variant: "error",
            });
            return;
          }
        }

        // Check for blank name error
        if (
          responseData?.name?.some((msg) =>
            msg.includes("This field may not be blank"),
          )
        ) {
          enqueueSnackbar("Tile name cannot be empty. Please enter a title.", {
            variant: "error",
          });
          return;
        }
      }

      enqueueErrorSnackbar(error, "Failed to update tile");
    },
  });
}

export type AssignFramesMode = "move" | "copy";

export type AssignFramesIntent = "assign" | "remove";

export type AssignFramesArgs = {
  siteId: SiteId;
  dragonTileId: DragonTileId;
  mode: AssignFramesMode;
  targetInnerTileId: InnerTileId;
  frames: Frame[];
  intent?: AssignFramesIntent;
  sourceLabel?: string;
  copyMetadata?: boolean;
  suppressSuccessToast?: boolean;
};

export function useAssignFrames() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<Frame[], Error, AssignFramesArgs>({
    mutationFn: async ({
      siteId,
      mode,
      targetInnerTileId,
      frames,
      copyMetadata,
    }) => {
      if (mode === "move") {
        return Promise.all(
          frames.map((frame) =>
            updateFrame(siteId, frame.id, { inner_tile: targetInnerTileId }),
          ),
        );
      }
      const timestamp = dayjs().format("MM/DD/YYYY h:mm A");
      const hydratedFrames = await Promise.all(
        frames.map((frame) =>
          queryClient.ensureQueryData(frameQueryOptions(siteId, frame.id)),
        ),
      );
      return Promise.all(
        hydratedFrames.map((frame) =>
          createFrame(
            siteId,
            createFramePayloadSchema.parse({
              name: `${timestamp} - ${frame.name}`,
              is_active: frame.is_active,
              site: siteId,
              inner_tile: targetInnerTileId,
              frame_variants: frame.frame_variants
                .filter((variant) => !!variant.medium)
                .map((variant) => ({
                  medium: variant.medium,
                  editor_type: variant.editor_type,
                  content_text: variant.content_text,
                  content_url: variant.content_url,
                  content_json: variant.content_json,
                  content_plaintext: variant.content_plaintext ?? null,
                  config: variant.config,
                })),
              metadata:
                copyMetadata && frame.metadata ? frame.metadata : undefined,
            }),
          ),
        ),
      );
    },
    onSuccess: (
      resultFrames,
      {
        mode,
        frames,
        siteId,
        dragonTileId,
        targetInnerTileId,
        intent,
        sourceLabel,
        suppressSuccessToast,
      },
    ) => {
      const count = frames.length;
      const noun = count === 1 ? "tile" : "tiles";
      const message =
        intent === "remove"
          ? `Removed ${count} ${noun}${sourceLabel ? ` from ${sourceLabel}` : ""}`
          : `${mode === "move" ? "Moved" : "Copied"} ${count} ${noun}`;
      if (!suppressSuccessToast) {
        enqueueSnackbar(message, { variant: "success" });
      }

      const sourceInnerTileIds = new Set(frames.map((f) => f.inner_tile));
      const movedIds = new Set(frames.map((f) => f.id));

      queryClient.setQueryData<DragonTile>(
        dragonTilesKeys.detail(siteId, dragonTileId),
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            inner_tiles: prev.inner_tiles.map((it) => {
              if (it.id === targetInnerTileId) {
                const toRef = (f: Frame): InnerTileFrameRef => ({
                  id: f.id,
                  name: f.name,
                  is_active: f.is_active,
                  events: f.metadata?.events ?? [],
                });
                const additions: InnerTileFrameRef[] =
                  mode === "copy" ? resultFrames.map(toRef) : frames.map(toRef);
                return { ...it, frames: [...it.frames, ...additions] };
              }
              if (mode === "move" && sourceInnerTileIds.has(it.id)) {
                return {
                  ...it,
                  frames: it.frames.filter((ref) => !movedIds.has(ref.id)),
                };
              }
              return it;
            }),
          };
        },
      );

      if (mode === "move") {
        queryClient.setQueriesData<GetFramesResponse>(
          { queryKey: ["frames", siteId] },
          (prev) => {
            if (!prev?.results) return prev;
            const kept = prev.results.filter(
              (frame) => !movedIds.has(frame.id),
            );
            const removed = prev.results.length - kept.length;
            if (removed === 0) return prev;
            return {
              ...prev,
              results: kept,
              count: Math.max(0, prev.count - removed),
            };
          },
        );
      }

      void queryClient.invalidateQueries({
        queryKey: dragonTilesKeys.detail(siteId, dragonTileId),
      });
      void queryClient.invalidateQueries({ queryKey: ["frames", siteId] });
      invalidateFramePreviews(queryClient, siteId);
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to assign tiles");
    },
  });
}

type ApprovalStatus = "approved" | "pending";

/**
 * Canonical write path for a frame's active/approval state.
 *
 * `is_active` and `approval_status` must always move together —
 * `is_active === (approval_status === "approved")` (see the `Frame` type in
 * `@/types/dragon-tiles`). `is_active` is what the backend actually honors;
 * `approval_status` is FE-only. This hook always sets both from the single
 * `next` argument, both in the network payload and the optimistic cache
 * update, so the two flags cannot drift. Prefer this over patching either
 * field directly.
 */
export function useToggleApproval(
  siteId: string,
  frameId: string,
): {
  toggleApproval: (next: ApprovalStatus) => void;
  isUpdatingFrame: boolean;
} {
  const qc = useQueryClient();
  const queryKey = frameQueryOptions(siteId, frameId).queryKey;

  const { mutate: toggleApproval, isPending: isUpdatingFrame } = useMutation({
    mutationFn: async (next: ApprovalStatus) => {
      return updateFrame(siteId, frameId, {
        approval_status: next,
        is_active: next === "approved",
      });
    },
    onMutate: async (next: ApprovalStatus) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<Frame>(queryKey);

      if (previous) {
        qc.setQueryData<Frame>(queryKey, {
          ...previous,
          approval_status: next,
          is_active: next === "approved",
        });
      }

      return { previous };
    },
    onSuccess: (updatedFrame) => {
      // Update cache with server response to get the new modify_date
      qc.setQueryData<Frame>(queryKey, updatedFrame);
      // Invalidate frames list queries so the card checkmark (approved status) re-renders
      void qc.invalidateQueries({ queryKey: ["frames", siteId] });
      void qc.invalidateQueries({ queryKey: dragonTilesKeys.all(siteId) });
      invalidateFramePreviews(qc, siteId);
    },
    onError: (error, _next, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
      enqueueErrorSnackbar(error, "Failed to update tile");
    },
  });

  return { toggleApproval, isUpdatingFrame } as const;
}

interface DeleteFrameErrorResponse {
  error?: string;
  message?: string;
  blocked_variants?: Array<{
    variant_id?: string;
    medium?: string;
    active_broadcasts?: {
      email_blasts?: string[];
      sms_blasts?: string[];
    };
  }>;
}

export function useDeleteFrame() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation<
    void,
    Error,
    { siteId: SiteId; frameId: string; innerTileId: InnerTileId }
  >({
    // We are not using innerTileId directly in the mutation - passing it through to callbacks like onSuccess

    mutationFn: ({ siteId, frameId }) => deleteFrame(siteId, frameId),
    onSuccess: (_, { siteId, frameId, innerTileId }) => {
      enqueueSnackbar("Tile deleted!", { variant: "success" });

      // Get frame from cache before invalidating to invalidate specific variant previews
      const frame = queryClient.getQueryData<Frame>(["frame", siteId, frameId]);

      void queryClient.invalidateQueries({
        queryKey: ["frame", siteId, frameId],
      });

      // Invalidate only the specific frame's variant previews instead of all bulk previews
      if (frame) {
        invalidateFrameVariantPreviews(queryClient, siteId, frame);
      } else {
        // Fallback: if frame not available, invalidate all bulk previews
        void queryClient.invalidateQueries({
          queryKey: ["bulkFrameVariantPreviews", siteId],
        });
      }

      void queryClient.invalidateQueries({
        queryKey: ["allFrames", siteId],
      });

      void queryClient.invalidateQueries({
        queryKey: ["framesWithPreviews", siteId],
      });

      // Invalidate paginated tiles query
      void queryClient.invalidateQueries({
        queryKey: ["frames", siteId],
      });

      invalidateFramePreviews(queryClient, siteId);

      // Invalidate deleted frames query
      void queryClient.invalidateQueries({
        queryKey: ["deletedFrames", siteId],
      });

      const dragonTiles: DragonTile[] | undefined = queryClient.getQueryData([
        "dragonTiles",
        siteId,
      ]);
      const parentTile = dragonTiles?.find((tile) =>
        tile.inner_tiles.find((innerTile) => innerTile.id === innerTileId),
      );
      if (parentTile) {
        void queryClient.invalidateQueries({
          queryKey: dragonTilesKeys.detail(siteId, parentTile.id),
        });
      }
    },
    onError: (error) => {
      if (isAxiosError<DeleteFrameErrorResponse>(error)) {
        const errorData = error.response?.data;
        const status = error.response?.status;

        // Handle 400 error for tiles linked to active broadcasts
        if (status === 400 && errorData?.blocked_variants) {
          const allBroadcasts = new Set<string>();
          errorData.blocked_variants.forEach((variant) => {
            variant.active_broadcasts?.email_blasts?.forEach((name) =>
              allBroadcasts.add(name),
            );
            variant.active_broadcasts?.sms_blasts?.forEach((name) =>
              allBroadcasts.add(name),
            );
          });

          if (allBroadcasts.size > 0) {
            const broadcastList = Array.from(allBroadcasts).join(", ");
            enqueueSnackbar(
              `This tile can't be deleted because it's linked to an active or pending broadcast(s): ${broadcastList}`,
              {
                variant: "error",
                autoHideDuration: 8000,
              },
            );
            return;
          }
        }

        // Fallback to API error message if available
        enqueueErrorSnackbar(error, "Failed to delete tile");
        return;
      }

      // Generic error fallback for non-Axios errors
      enqueueErrorSnackbar(error, "Failed to delete tile");
    },
  });
}

// --- Email Preview Rendering ---

export type GetRendererFrameVariantHTMLPreviewRequest = {
  medium: ChannelMedium;
  content_text: string;
  /** Plain-text body; rendered into `plaintext_body` for true-plaintext variants. */
  content_plaintext?: string;
  config: Record<string, unknown>;
  /** Optional ENG-1291 personalization; omit property entirely when unset. */
  preview_fill_params?: PreviewFillParams;
};

export type RendererFrameVariantEmailPreview = {
  subject_line: string;
  html_body: string;
  /** Rendered preheader (ENG-1416); absent on gateways older than event-schemas v1.24.5. */
  preview_text?: string;
  /** Rendered plain-text body (ENG-1415); set for true-plaintext variants. */
  plaintext_body?: string;
};

export type GetRendererFrameVariantHTMLPreviewResponse = {
  medium: string;
  email_preview: RendererFrameVariantEmailPreview;
  /** Present when Connect returns ENG-1291 provenance fields. */
  used_fallback_data?: boolean;
  fallback_reason?: PreviewFallbackReason;
};

export function parseRendererFrameVariantHtmlPreviewResponse(
  data: unknown,
): GetRendererFrameVariantHTMLPreviewResponse {
  if (!data || typeof data !== "object") {
    throw new Error(
      "Invalid frame variant HTML preview response: expected object",
    );
  }
  const root = data as Record<string, unknown>;
  if (typeof root.medium !== "string") {
    throw new Error(
      "Invalid frame variant HTML preview response: missing medium",
    );
  }
  const emailPreview = root.email_preview;
  if (!emailPreview || typeof emailPreview !== "object") {
    throw new Error(
      "Invalid frame variant HTML preview response: missing email_preview",
    );
  }
  const ep = emailPreview as Record<string, unknown>;
  if (typeof ep.subject_line !== "string") {
    throw new Error(
      "Invalid frame variant HTML preview response: missing subject_line",
    );
  }
  if (typeof ep.html_body !== "string") {
    throw new Error(
      "Invalid frame variant HTML preview response: missing html_body",
    );
  }

  const usedFallback =
    typeof root.used_fallback_data === "boolean"
      ? root.used_fallback_data
      : undefined;
  const fallbackReason =
    typeof root.fallback_reason === "string" && root.fallback_reason.length > 0
      ? root.fallback_reason
      : undefined;

  return {
    medium: root.medium,
    email_preview: {
      subject_line: ep.subject_line,
      html_body: ep.html_body,
      ...(typeof ep.preview_text === "string"
        ? { preview_text: ep.preview_text }
        : {}),
      ...(typeof ep.plaintext_body === "string"
        ? { plaintext_body: ep.plaintext_body }
        : {}),
    },
    ...(usedFallback !== undefined ? { used_fallback_data: usedFallback } : {}),
    ...(fallbackReason !== undefined
      ? { fallback_reason: fallbackReason }
      : {}),
  };
}

export async function getRenderedFrameVariantHTMLPreview(
  siteId: string,
  body: GetRendererFrameVariantHTMLPreviewRequest,
): Promise<GetRendererFrameVariantHTMLPreviewResponse> {
  const { preview_fill_params: fillParams, ...rest } = body;
  const payload: Record<string, unknown> = { ...rest };
  if (fillParams) {
    payload.preview_fill_params = fillParams;
  }
  const response = await withJWTClient((client) =>
    client.post(
      `${CONNECT_API_V1_URL}/sites/${siteId}/preview/frame-variant`,
      payload,
    ),
  );
  return parseRendererFrameVariantHtmlPreviewResponse(response.data);
}

export const getFrameBySlug = async (
  siteId: string,
  slug: "blank-template" | "broadcast-template",
): Promise<Frame[]> => {
  // The gateway filters by slug server-side (slug is unique per site), so this
  // is a single lookup instead of paging through every frame to find a match.
  const params = buildFrameListParams({
    limit: FRAMES_PAGE_SIZE,
    offset: 0,
    slug,
  });
  const listResponse = await withJWTClient((client) =>
    client.get<GatewayRetrieveAllFramesResponse>(
      `${gatewayUrl(siteId, "frames")}?${params.toString()}`,
    ),
  );
  const match = (listResponse.data.frames ?? []).find(
    (frame) => frame.slug === slug,
  );
  if (!match) return [];
  const frame = await getFrame(siteId, match.rj_id || match.id);
  return [frame];
};

type TemplateQueryResult = {
  content: unknown;
  variantCount: number;
};

export const useTemplateFrame = (
  siteId: string,
  slug: "blank-template" | "broadcast-template",
) => {
  const query = useQuery<
    Frame[],
    Error,
    TemplateQueryResult,
    [string, string, string]
  >({
    queryKey: ["frame", siteId, slug],
    queryFn: () => getFrameBySlug(siteId, slug),
    enabled: Boolean(siteId),
    placeholderData: (prev) => prev,
    select: (frames): TemplateQueryResult => ({
      content: frames?.[0]?.frame_variants?.[0]?.content_json ?? null,
      variantCount: frames?.[0]?.frame_variants?.length ?? 0,
    }),
  });

  return {
    ...query,
    data: query.data,
    loading: query.isLoading,
    error: query.isError ? query.error : null,
  };
};

// connect-api's RetrieveAllFrames is offset/limit paginated and returns `total`;
// the rj2 consumers paginate on `count`, so echo total there. Internal template
// frames are still filtered client-side, mirroring the rj2 path.
const toFramesPageResponse = (
  data: GatewayRetrieveAllFramesResponse,
  siteId: SiteId,
): GetFramesResponse => {
  const mapped = (data.frames ?? []).map(fromGatewayFrameListing);
  const filtered = filterExcludedFrames(mapped, siteId);
  const excludedCount = mapped.length - filtered.length;
  return {
    count: (data.total ?? 0) - excludedCount,
    next: null,
    previous: null,
    results: filtered,
  };
};

// connect-api expects ids as repeated `ids=<rjId>` query params; a comma-joined
// value is split so each id becomes its own param.
const buildFrameListParams = (parts: {
  limit: number;
  offset: number;
  search?: string;
  medium?: ChannelMedium;
  order_by?: "create_date" | "modify_date" | "name" | "is_active";
  order?: "asc" | "desc";
  dragon_tile?: string;
  inner_tile?: string;
  slug?: string;
  ids?: string;
  has_part?: boolean;
  is_active?: boolean;
  /** Filters to frames with metadata.events entry matching name + active: 1 */
  event?: string;
  include_deleted?: boolean;
  deleted_only?: boolean;
  device?: FramePreviewDevice;
}): URLSearchParams => {
  const params = new URLSearchParams({
    limit: String(parts.limit),
    offset: String(parts.offset),
  });
  if (parts.include_deleted) params.append("include_deleted", "true");
  if (parts.deleted_only) params.append("deleted_only", "true");
  if (parts.search) params.append("search", parts.search);
  if (parts.medium) params.append("medium", parts.medium);
  if (parts.order_by) params.append("order_by", parts.order_by);
  if (parts.order) params.append("order", parts.order);
  if (parts.dragon_tile) params.append("dragon_tile", parts.dragon_tile);
  if (parts.inner_tile) params.append("inner_tile", parts.inner_tile);
  if (parts.slug) params.append("slug", parts.slug);
  if (parts.has_part !== undefined)
    params.append("has_part", String(parts.has_part));
  if (parts.is_active !== undefined)
    params.append("is_active", String(parts.is_active));
  if (parts.event) params.append("event", parts.event);
  if (parts.device) params.append("device", parts.device);
  if (parts.ids) {
    for (const id of parts.ids.split(",")) {
      if (id.length > 0) params.append("ids", id);
    }
  }
  return params;
};

// Function to get a page of tiles for a site
export const getFramesPage = async (
  siteId: SiteId,
  page = 1,
  pageSize = FRAMES_PAGE_SIZE,
  filters?: {
    search?: string;
    medium?: ChannelMedium;
    dragon_tile?: string;
    inner_tile?: string;
    order_by?: "create_date" | "modify_date" | "name" | "is_active";
    order?: "asc" | "desc";
    ids?: string;
    has_part?: boolean;
    is_active?: boolean;
    /** Filters to frames with metadata.events entry matching name + active: 1 */
    event?: string;
    exclude_content_text?: boolean;
  },
): Promise<GetFramesResponse> => {
  // connect-api's RetrieveAllFrames supports search/medium/order_by/order +
  // limit/offset plus dragon_tile/inner_tile/ids/has_part/is_active/event.
  // exclude_content_text has no gateway equivalent and is moot (the slim
  // FrameListing never carries content_text).
  const params = buildFrameListParams({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: filters?.search,
    medium: filters?.medium,
    order_by: filters?.order_by,
    order: filters?.order,
    dragon_tile: filters?.dragon_tile,
    inner_tile: filters?.inner_tile,
    ids: filters?.ids,
    has_part: filters?.has_part,
    is_active: filters?.is_active,
    event: filters?.event,
  });

  const response = await withJWTClient((client) =>
    client.get<GatewayRetrieveAllFramesResponse>(
      `${gatewayUrl(siteId, "frames")}?${params.toString()}`,
    ),
  );
  return toFramesPageResponse(response.data, siteId);
};

export const framesPageQueryOptions = (
  siteId: SiteId,
  page = 1,
  pageSize = FRAMES_PAGE_SIZE,
  filters?: {
    search?: string;
    medium?: ChannelMedium;
    dragon_tile?: string;
    inner_tile?: string;
    order_by?: "create_date" | "modify_date" | "name" | "is_active";
    order?: "asc" | "desc";
    ids?: string;
    has_part?: boolean;
    is_active?: boolean;
    /** Filters to frames with metadata.events entry matching name + active: 1 */
    event?: string;
    exclude_content_text?: boolean;
  },
) => ({
  queryKey: ["frames", siteId, page, pageSize, filters] as const,
  queryFn: (): Promise<GetFramesResponse> =>
    getFramesPage(siteId, page, pageSize, filters),
  keepPreviousData: true,
  staleTime: 30_000, // 30 seconds - prevents refetch right after loader prefetch
});

// Function to get a page of deleted tiles for a site
export const getDeletedFramesPage = async (
  siteId: SiteId,
  page = 1,
  pageSize = FRAMES_PAGE_SIZE,
  filters?: {
    search?: string;
    medium?: ChannelMedium;
    dragon_tile?: string;
    order_by?: "create_date" | "modify_date" | "name";
    order?: "asc" | "desc";
  },
): Promise<GetFramesResponse> => {
  const params = buildFrameListParams({
    deleted_only: true,
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: filters?.search,
    medium: filters?.medium,
    order_by: filters?.order_by,
    order: filters?.order,
    dragon_tile: filters?.dragon_tile,
  });

  const response = await withJWTClient((client) =>
    client.get<GatewayRetrieveAllFramesResponse>(
      `${gatewayUrl(siteId, "frames")}?${params.toString()}`,
    ),
  );
  return toFramesPageResponse(response.data, siteId);
};

export const deletedFramesPageQueryOptions = (
  siteId: SiteId,
  page = 1,
  pageSize = FRAMES_PAGE_SIZE,
  filters?: {
    search?: string;
    medium?: ChannelMedium;
    dragon_tile?: string;
    order_by?: "create_date" | "modify_date" | "name";
    order?: "asc" | "desc";
  },
) => ({
  queryKey: ["deletedFrames", siteId, page, pageSize, filters] as const,
  queryFn: (): Promise<GetFramesResponse> =>
    getDeletedFramesPage(siteId, page, pageSize, filters),
  keepPreviousData: true,
});

const framePreviewManifestSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("image"),
    card_url: z.string().min(1),
    full_url: z.string().min(1),
  }),
  z.object({ state: z.literal("fallback") }),
]);

const toFramePreviewsPageResponse = (
  data: GatewayRetrieveAllFramePreviewsResponse,
  siteId: SiteId,
): GetFramePreviewsResponse => {
  let manifestParseFailed = false;
  const parseManifest = (raw: unknown): FramePreviewManifest | null => {
    if (raw == null) return null;
    const parsed = framePreviewManifestSchema.safeParse(raw);
    if (parsed.success) return parsed.data;
    manifestParseFailed = true;
    return { state: "fallback" };
  };
  const mapped = (data.frame_previews ?? []).map((frame) =>
    fromGatewayFramePreviewListing(frame, parseManifest),
  );
  if (manifestParseFailed) {
    enqueueSnackbar("Couldn't load some previews. Please try again.", {
      variant: "error",
    });
  }
  const filtered = filterExcludedFrames(mapped, siteId);
  const excludedCount = mapped.length - filtered.length;
  return {
    count: (data.total ?? 0) - excludedCount,
    next: null,
    previous: null,
    results: filtered,
  };
};

/** Which render of a frame the previews endpoint should return. */
export type FramePreviewDevice = "mobile" | "desktop";

export type FramePreviewsFilters = {
  search?: string;
  medium?: ChannelMedium;
  dragon_tile?: string;
  inner_tile?: string;
  order_by?: "create_date" | "modify_date" | "name" | "is_active";
  order?: "asc" | "desc";
  ids?: string;
  has_part?: boolean;
  is_active?: boolean;
  deleted_only?: boolean;
  device?: FramePreviewDevice;
  /** Filters to frames with metadata.events entry matching name + active: 1 */
  event?: string;
};

export const getFramePreviewsPage = async (
  siteId: SiteId,
  page = 1,
  pageSize = FRAMES_PAGE_SIZE,
  filters?: FramePreviewsFilters,
): Promise<GetFramePreviewsResponse> => {
  const params = buildFrameListParams({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: filters?.search,
    medium: filters?.medium,
    order_by: filters?.order_by,
    order: filters?.order,
    dragon_tile: filters?.dragon_tile,
    inner_tile: filters?.inner_tile,
    ids: filters?.ids,
    has_part: filters?.has_part,
    is_active: filters?.is_active,
    deleted_only: filters?.deleted_only,
    device: filters?.device,
    event: filters?.event,
  });

  const response = await withJWTClient((client) =>
    client.get<GatewayRetrieveAllFramePreviewsResponse>(
      `${CONNECT_API_V1_URL}/sites/${siteId}/frame-previews?${params.toString()}`,
    ),
  );
  return toFramePreviewsPageResponse(response.data, siteId);
};

export const framePreviewsInfiniteQueryOptions = (
  siteId: SiteId,
  pageSize = FRAMES_PAGE_SIZE,
  filters?: FramePreviewsFilters,
) => ({
  queryKey: ["framePreviews", siteId, "infinite", pageSize, filters] as const,
  initialPageParam: 1,
  queryFn: ({ pageParam }: { pageParam: number }) =>
    getFramePreviewsPage(siteId, pageParam, pageSize, filters),
  getNextPageParam: (
    lastPage: GetFramePreviewsResponse,
    allPages: GetFramePreviewsResponse[],
  ) => {
    const totalPages = Math.max(
      1,
      Math.ceil((lastPage?.count ?? 0) / pageSize),
    );
    const loadedPages = allPages.length;
    return loadedPages < totalPages ? loadedPages + 1 : undefined;
  },
  staleTime: 30_000,
});

export const framePreviewsPageQueryOptions = (
  siteId: SiteId,
  page = 1,
  pageSize = FRAMES_PAGE_SIZE,
  filters?: FramePreviewsFilters,
) => ({
  queryKey: ["framePreviews", siteId, page, pageSize, filters] as const,
  queryFn: (): Promise<GetFramePreviewsResponse> =>
    getFramePreviewsPage(siteId, page, pageSize, filters),
  staleTime: 30_000,
});

// Bulk tile variant previews
export type BulkFrameVariantPreviewsPayload = {
  frame_variant_ids: string[];
};

export type FrameVariantBulkPreview = {
  medium: ChannelMedium;
  body: string;
  textPreviewContent: TextComposerPreviewContent | null;
};

export type BulkFrameVariantPreviewsResponse = {
  [frameVariantId: string]: FrameVariantBulkPreview;
};

const textPreviewContentSchema = z
  .object({
    body: z
      .object({
        type: z.enum(["TEXT", "IMAGE", "DOCUMENT"]).optional(),
        text: z.string().optional(),
        url: z.string().optional(),
      })
      .optional(),
    buttons: z
      .array(
        z.object({
          type: z.enum(["OPEN_URL"]).optional(),
          text: z.string().optional(),
          url: z.string().optional(),
        }),
      )
      .optional(),
  })
  .nullable();

const bulkFrameVariantPreviewRowSchema = z
  .object({
    frame_variant_id: z.string(),
    medium: z.string().optional(),
    email_preview: z
      .object({
        html_body: z.string().default(""),
      })
      .optional(),
    text_preview: z
      .object({
        medium: z.string(),
        content_json: z.object({
          transformed: z.object({
            content: textPreviewContentSchema,
          }),
        }),
      })
      .optional(),
  })
  .transform(({ frame_variant_id, medium, email_preview, text_preview }) => ({
    frameVariantId: frame_variant_id,
    medium: (medium ?? "email") as ChannelMedium,
    body: email_preview?.html_body ?? "",
    textPreviewContent:
      (text_preview?.content_json.transformed
        .content as TextComposerPreviewContent | null) ?? null,
  }));

const bulkFrameVariantPreviewsSchema = z.object({
  previews: z.array(z.unknown()),
});

const BULK_FRAME_VARIANT_PREVIEW_TIMEOUT_SECONDS = 10;
// TODO: update the endpoint to end in /previews
export const getBulkFrameVariantPreviews = async (
  siteId: SiteId,
  payload: BulkFrameVariantPreviewsPayload,
): Promise<BulkFrameVariantPreviewsResponse> => {
  const response = await withJWTClient((client) =>
    client.post(`${CONNECT_API_V1_URL}/sites/${siteId}/previews`, {
      ...payload,
      timeout: BULK_FRAME_VARIANT_PREVIEW_TIMEOUT_SECONDS,
    }),
  );
  const parsedResponse = bulkFrameVariantPreviewsSchema.safeParse(
    response.data,
  );

  if (!parsedResponse.success) {
    enqueueSnackbar("Couldn't load previews. Please try again.", {
      variant: "error",
    });
    return {};
  }

  return Object.fromEntries(
    parsedResponse.data.previews.flatMap((preview) => {
      const parsedPreview = bulkFrameVariantPreviewRowSchema.safeParse(preview);
      return parsedPreview.success
        ? [
            [
              parsedPreview.data.frameVariantId,
              {
                medium: parsedPreview.data.medium,
                body: parsedPreview.data.body,
                textPreviewContent: parsedPreview.data.textPreviewContent,
              } satisfies FrameVariantBulkPreview,
            ] as const,
          ]
        : [];
    }),
  );
};

// Query options for bulk tile variant previews
export const bulkFrameVariantPreviewsQueryOptions = (
  siteId: string,
  frameVariantIds: string[],
) => {
  // Sort variant IDs for stable query key - prevents cache misses when array order changes
  const sortedIds = [...frameVariantIds].sort();
  return {
    queryKey: ["bulkFrameVariantPreviews", siteId, sortedIds] as const,
    queryFn: () =>
      getBulkFrameVariantPreviews(siteId, {
        frame_variant_ids: frameVariantIds,
      }),
    enabled: frameVariantIds.length > 0,
    staleTime: 30_000, // 30 seconds - previews change less frequently
  };
};

// Individual frame variant preview query options
// This allows per-item caching and invalidation
export const frameVariantPreviewQueryOptions = (
  siteId: string,
  frameVariantId: string,
) => ({
  queryKey: ["frameVariantPreview", siteId, frameVariantId],
  queryFn: async () => {
    const response = await getBulkFrameVariantPreviews(siteId, {
      frame_variant_ids: [frameVariantId],
    });
    return (
      response[frameVariantId] || {
        medium: "email" as ChannelMedium,
        body: "",
        textPreviewContent: null,
      }
    );
  },
  enabled: !!frameVariantId,
});

export const invalidateFramePreviews = (
  queryClient: QueryClient,
  siteId: string,
  refetchType?: "active" | "inactive" | "all" | "none",
) => {
  void queryClient.invalidateQueries({
    queryKey: ["framePreviews", siteId],
    ...(refetchType ? { refetchType } : {}),
  });
};

// Helper function to invalidate variant previews for a specific frame
// This allows per-item invalidation instead of invalidating all bulk previews
export const invalidateFrameVariantPreviews = (
  queryClient: QueryClient,
  siteId: string,
  frame: Frame,
) => {
  // Invalidate previews for all email variants of this frame
  const emailVariantIds = frame.frame_variants
    .filter((variant) => variant.id && variant.medium === "email")
    .map((variant) => variant.id);

  for (const variantId of emailVariantIds) {
    void queryClient.invalidateQueries({
      queryKey: ["frameVariantPreview", siteId, variantId],
    });
  }

  // Also invalidate bulk previews that might contain this frame's variants
  // This ensures backward compatibility with components still using bulk queries
  void queryClient.invalidateQueries({
    queryKey: ["bulkFrameVariantPreviews", siteId],
  });
};

// --- Tile Variant Versions ---
export const getFrameVariantVersions = async (
  siteId: SiteId,
  variantId: FrameVariantId,
): Promise<GetFrameVariantVersionsResponse> => {
  const response = await withJWTClient((client) =>
    client.get<GatewayFrameVariantVersionsResponse>(
      gatewayUrl(siteId, "frame-variants", variantId, "versions"),
    ),
  );
  return fromGatewayFrameVariantVersions(response.data);
};

export const frameVariantVersionsQueryOptions = (
  siteId: string,
  variantId: string,
) => ({
  queryKey: ["frameVariantVersions", siteId, variantId],
  queryFn: () => getFrameVariantVersions(siteId, variantId),
  enabled: !!siteId && !!variantId,
});

export function useFrameVariantVersions(
  siteId: SiteId,
  variantId: FrameVariantId,
  options?: Omit<
    UseQueryOptions<GetFrameVariantVersionsResponse, Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    ...frameVariantVersionsQueryOptions(siteId, variantId),
    ...options,
  });
}

export type {
  PreviewFallbackReason,
  PreviewFillParams,
} from "./previewUserContext";
export { previewUserContextKeyParts } from "./previewUserContext";
